import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { useAuthStore } from '../store/authStore';

const capabilities = [
  ['Inventory intelligence', 'Consumption-based reorder alerts, multi-unit catalog, packing and barcode support.'],
  ['Dealer network', 'Dealers, sub-dealers, distributors, suppliers and shopkeepers in one controlled hierarchy.'],
  ['Live delivery operations', 'Salesman location, shipment milestones and confirmed final-destination delivery.'],
  ['Complete business ERP', 'Sales, procurement, finance, HR, projects, quality, assets and reporting.'],
];

export default function HomePage() {
  const { isAuthenticated } = useAuthStore();
  const [companySlug, setCompanySlug] = useState('default');
  const navigate = useNavigate();

  const workspace = `/${companySlug.trim().toLowerCase() || 'default'}`;

  return (
    <div className="page-shell bg-gradient-to-br from-surface via-white to-primary-50/40">
      <header className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 flex items-center justify-between gap-3">
        <Logo size="md" />
        <div className="flex items-center gap-2 sm:gap-3">
          <Link to={`${workspace}/setup`} className="hidden sm:inline-block px-4 py-2 text-sm text-content-secondary hover:text-primary-600 transition-colors">Initial setup</Link>
          <Link to={isAuthenticated ? `${workspace}/modules` : `${workspace}/login`} className="btn-primary px-4 sm:px-5 py-2.5">
            {isAuthenticated ? 'Open workspace' : 'Sign in'}
          </Link>
        </div>
      </header>

      <main>
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 lg:py-28 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <div className="inline-flex rounded-full border border-primary-200 bg-primary-50 text-primary-700 px-3 py-1 text-xs font-semibold mb-6">
              Operations from source to final destination
            </div>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold leading-tight text-content">
              Run your business with one clear operational picture.
            </h1>
            <p className="text-lg text-content-secondary mt-6 max-w-xl">
              SMEPRO360 connects inventory, procurement, dealer distribution, field sales and delivery confirmation with your core ERP.
            </p>

            <div className="mt-6 card p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
              <label className="flex-1 text-sm font-medium text-content">
                Your company workspace
                <input
                  value={companySlug}
                  onChange={e => setCompanySlug(e.target.value.replace(/\s+/g, '-').toLowerCase())}
                  className="input-field mt-1.5 font-mono"
                  placeholder="company-slug"
                />
              </label>
              <button
                type="button"
                onClick={() => navigate(`${workspace}/login`)}
                className="btn-primary px-6 py-2.5 whitespace-nowrap"
              >
                Go to workspace
              </button>
            </div>

            <div className="flex flex-wrap gap-3 mt-6">
              <Link to={isAuthenticated ? `${workspace}/modules` : `${workspace}/login`} className="btn-primary px-6 py-3">
                {isAuthenticated ? 'Go to modules' : 'Access SMEPRO360'}
              </Link>
              <Link to={`${workspace}/setup`} className="btn-secondary px-6 py-3">
                Configure company
              </Link>
            </div>
            <p className="text-xs text-content-muted mt-3">
              Example URL: <code className="bg-surface-muted px-1.5 py-0.5 rounded">smepro360.com/{companySlug || 'your-company'}/login</code>
            </p>
          </div>
          <div className="card p-6 shadow-card-hover">
            <div className="grid sm:grid-cols-2 gap-4">
              {capabilities.map(([title, text]) => (
                <div key={title} className="rounded-2xl bg-surface-muted border border-border p-5">
                  <div className="w-9 h-9 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center mb-4 font-semibold">✓</div>
                  <h2 className="font-semibold text-content">{title}</h2>
                  <p className="text-sm text-content-secondary mt-2 leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
